import './styles.sass';

import React from 'react';
import PropTypes from 'prop-types';

// Contains a part of Explorer that throws while rendering, showing message in its place, so the rest of Explorer
// stays usable instead of React unmounting the whole app. Changing resetKey clears the error, so a page given one per
// navigation recovers when the user moves on or repeats the same search. It clears only the error rather than
// remounting via key, which would reset every page's state on each navigation. An error caught here never reaches
// Sentry's global handlers in production, so report it explicitly.
class ErrorBoundary extends React.Component {
  static propTypes = {
    message: PropTypes.string.isRequired,
    resetKey: PropTypes.string,
    children: PropTypes.node.isRequired
  };

  state = { hasError: false, resetKey: this.props.resetKey };

  // A flag rather than the thrown value, which can be falsy (throw undefined) and would then read as no error
  static getDerivedStateFromError() {
    return { hasError: true };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.resetKey !== state.resetKey) return { hasError: false, resetKey: props.resetKey };
    return null;
  }

  componentDidCatch(error, info) {
    try {
      window.Sentry?.captureException(error, { contexts: { react: { componentStack: info.componentStack } } });
    } catch {
      // An error thrown here would escape this boundary, and nothing above it would catch it
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <p className="Explorer-ErrorBoundary-message" role="alert">
          {this.props.message}
        </p>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
